import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const modeOptions = ['disabled', 'inline', 'exclusive'] as const;
type CustomPromptAudioSupportMode = (typeof modeOptions)[number];

export const CustomPromptAudioSupport = () => {
  const [updateConfig, { isLoading: isUpdating }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const savedMode = (remoteAppConfig
    ?.ff_customPromptAudioSupportMode ??
    'disabled') as CustomPromptAudioSupportMode;

  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const onSaveMode = useCallback(
    async (mode: CustomPromptAudioSupportMode) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data: {
            ff_customPromptAudioSupportMode: mode,
          },
        },
        {
          onSettled,
        },
      );
    },
    [onSettled, updateConfig],
  );

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">Custom prompt audio support</Typography>
        <Tooltip title="Controls custom prompt audio behavior: disabled (no audio), inline (mic in editor), or exclusive (Text or Audio decision on Select Report Type).">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
      >
        <FormControl size="small" disabled={isUpdating} sx={{ minWidth: 220 }}>
          <InputLabel>Mode</InputLabel>
          <Select
            value={savedMode}
            label="Mode"
            onChange={event => {
              const value = event.target
                .value as CustomPromptAudioSupportMode;
              void onSaveMode(value);
            }}
          >
            {modeOptions.map(mode => (
              <MenuItem key={mode} value={mode}>
                {mode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isUpdating && <CircularProgress size={14} />}
      </Box>
      <Box marginTop={1}>
        <Typography variant="body2" color="text.secondary">
          Current mode: {savedMode}
        </Typography>
      </Box>
    </Box>
  );
};
